import { useMemo, useState } from 'react'
import { CheckCircle2, Plus, ShieldAlert, Trash2, XCircle } from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  LoadingState,
  Modal,
  Select,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import {
  useApproveSlaCredit,
  useCreateSlaCredit,
  useCreateSlaProfile,
  useDeleteSlaCredit,
  useDeleteSlaProfile,
  useMarkSlaCreditInvoiced,
  usePreviewSlaCredit,
  useRejectSlaCredit,
  useSlaCredits,
  useSlaProfiles,
  useUpdateSlaProfile,
  type CreditTier,
  type CreateSlaCreditInput,
  type CreateSlaProfileInput,
  type SlaCreditPreview,
  type SlaCreditRow,
  type SlaProfile,
} from '../api/slaApi.ts'
import { useContracts } from '../api/contractsApi.ts'

const PERIOD_OPTIONS = [
  { value: 'monthly', label: 'Monatlich' },
  { value: 'quarterly', label: 'Quartalsweise' },
  { value: 'yearly', label: 'Jährlich' },
]

const DEFAULT_TIERS: CreditTier[] = [
  { thresholdPercentBp: 9900, creditPercentBp: 500 },
  { thresholdPercentBp: 9800, creditPercentBp: 1000 },
  { thresholdPercentBp: 9500, creditPercentBp: 2500 },
]

function fmtPercentBp(bp: number): string {
  return `${(bp / 100).toFixed(2).replace('.', ',')} %`
}

function fmtCents(cents: number, currency = 'EUR'): string {
  return (cents / 100).toLocaleString('de-DE', { style: 'currency', currency })
}

function firstOfMonthIso(monthsBack = 1): string {
  const d = new Date()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() - monthsBack)
  return d.toISOString().slice(0, 10)
}

function endOfMonthIso(monthsBack = 1): string {
  const d = new Date()
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() - monthsBack + 1)
  d.setUTCDate(0)
  return d.toISOString().slice(0, 10)
}

export function SlaSection() {
  return (
    <Tabs defaultValue="credits">
      <TabsList>
        <TabsTrigger value="credits">Gutschriften</TabsTrigger>
        <TabsTrigger value="profiles">SLA-Profile</TabsTrigger>
      </TabsList>
      <TabsContent value="credits">
        <SlaCreditsTab />
      </TabsContent>
      <TabsContent value="profiles">
        <SlaProfilesTab />
      </TabsContent>
    </Tabs>
  )
}

// ─── Profile-Tab ────────────────────────────────────────────────────────────

function SlaProfilesTab() {
  const { toast } = useToast()
  const { data = [], isLoading, error, refetch } = useSlaProfiles()
  const create = useCreateSlaProfile()
  const del = useDeleteSlaProfile()
  const [createOpen, setCreateOpen] = useState(false)
  const [editProfile, setEditProfile] = useState<SlaProfile | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<SlaProfile | null>(null)

  const columns: ColumnDef<SlaProfile, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Profil',
        cell: ({ row }) => (
          <div>
            <p className="text-small font-medium text-gray-900">{row.original.name}</p>
            {row.original.description && (
              <p className="text-caption text-gray-500">{row.original.description}</p>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'guaranteedAvailabilityBp',
        header: 'Garantierte Verfügbarkeit',
        size: 160,
        cell: ({ getValue }) => fmtPercentBp(getValue() as number),
      },
      {
        accessorKey: 'measurementPeriod',
        header: 'Messperiode',
        size: 120,
        cell: ({ getValue }) => {
          const v = getValue() as string
          return PERIOD_OPTIONS.find((o) => o.value === v)?.label ?? v
        },
      },
      {
        id: 'tiers',
        header: 'Credit-Stufen',
        size: 200,
        cell: ({ row }) => {
          const tiers = row.original.creditTiers
          if (tiers.length === 0) return <span className="text-caption text-gray-400">— keine —</span>
          return (
            <div className="flex flex-wrap gap-1">
              {tiers.map((t, i) => (
                <Badge key={i} variant="neutral" size="sm">
                  &lt; {fmtPercentBp(t.thresholdPercentBp)} → {fmtPercentBp(t.creditPercentBp)}
                </Badge>
              ))}
            </div>
          )
        },
      },
      {
        accessorKey: 'assignedContracts',
        header: 'Verträge',
        size: 90,
        cell: ({ getValue }) => <Badge variant="neutral" size="sm">{getValue() as number}</Badge>,
      },
      {
        accessorKey: 'active',
        header: 'Status',
        size: 90,
        cell: ({ getValue }) => (
          <Badge variant={(getValue() as boolean) ? 'success' : 'neutral'} size="sm">
            {(getValue() as boolean) ? 'Aktiv' : 'Inaktiv'}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        size: 110,
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="xs" onClick={() => setEditProfile(row.original)}>
              Bearbeiten
            </Button>
            <IconButton
              icon={<Trash2 className="w-4 h-4" />}
              aria-label="Löschen"
              variant="ghost"
              size="xs"
              onClick={() => setConfirmDelete(row.original)}
            />
          </div>
        ),
      },
    ],
    [],
  )

  if (isLoading) return <LoadingState label="Lade SLA-Profile …" />
  if (error) {
    return (
      <ErrorState
        description="SLA-Profile konnten nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => void refetch()}
      />
    )
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-h4 font-semibold text-gray-900">SLA-Profile</h3>
            <p className="text-small text-gray-500">
              Service-Level mit garantierter Verfügbarkeit, Reaktionszeiten und Credit-Stufen.
            </p>
          </div>
          <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
            Profil anlegen
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {data.length === 0 ? (
          <EmptyState
            title="Noch keine SLA-Profile"
            description="Lege ein Profil mit garantierter Verfügbarkeit und Credit-Stufen an, um es Verträgen zuzuordnen."
          />
        ) : (
          <DataTable columns={columns} data={data} />
        )}
      </Card.Body>

      <SlaProfileFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={(input) =>
          create.mutate(input, {
            onSuccess: () => {
              toast({ title: 'SLA-Profil angelegt', tone: 'success' })
              setCreateOpen(false)
            },
            onError: (err) =>
              toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
          })
        }
        saving={create.isPending}
      />

      {editProfile && (
        <SlaProfileFormModal
          open={true}
          profile={editProfile}
          onOpenChange={(o) => { if (!o) setEditProfile(null) }}
          onSubmit={() => undefined}
          saving={false}
        />
      )}

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="SLA-Profil löschen?"
        message={confirmDelete ? `Profil "${confirmDelete.name}" wird gelöscht.` : ''}
        variant="danger"
        confirmLabel="Löschen"
        loading={del.isPending}
        onConfirm={() => {
          if (!confirmDelete) return
          del.mutate(confirmDelete.id, {
            onSuccess: () => {
              toast({ title: 'Profil gelöscht', tone: 'success' })
              setConfirmDelete(null)
            },
            onError: (err) => {
              toast({ title: 'Löschen nicht möglich', description: err.message, tone: 'danger' })
              setConfirmDelete(null)
            },
          })
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </Card>
  )
}

// ─── Profil-Anlegen/Bearbeiten Modal ────────────────────────────────────────

interface ProfileFormProps {
  open: boolean
  profile?: SlaProfile
  onOpenChange: (o: boolean) => void
  onSubmit: (input: CreateSlaProfileInput) => void
  saving: boolean
}

function SlaProfileFormModal({ open, profile, onOpenChange, onSubmit, saving }: ProfileFormProps) {
  const update = useUpdateSlaProfile(profile?.id ?? '')
  const { toast } = useToast()
  const [name, setName] = useState(profile?.name ?? '')
  const [description, setDescription] = useState(profile?.description ?? '')
  const [availabilityPercent, setAvailabilityPercent] = useState(
    profile ? (profile.guaranteedAvailabilityBp / 100).toFixed(2).replace('.', ',') : '99,00',
  )
  const [period, setPeriod] = useState<'monthly' | 'quarterly' | 'yearly'>(profile?.measurementPeriod ?? 'monthly')
  const [reactionMinutes, setReactionMinutes] = useState(
    profile?.reactionTimeMinutes != null ? String(profile.reactionTimeMinutes) : '',
  )
  const [resolutionMinutes, setResolutionMinutes] = useState(
    profile?.resolutionTimeMinutes != null ? String(profile.resolutionTimeMinutes) : '',
  )
  const [tiers, setTiers] = useState<CreditTier[]>(profile?.creditTiers.length ? profile.creditTiers : DEFAULT_TIERS)
  const [active, setActive] = useState(profile?.active ?? true)

  const isEdit = Boolean(profile)

  function handleSubmit() {
    const guaranteedBp = Math.round(Number(availabilityPercent.replace(',', '.')) * 100)
    if (!name.trim()) {
      toast({ title: 'Name fehlt', tone: 'danger' })
      return
    }
    if (!Number.isFinite(guaranteedBp) || guaranteedBp < 0 || guaranteedBp > 10000) {
      toast({ title: 'Verfügbarkeit ungültig', tone: 'danger' })
      return
    }
    const payload: CreateSlaProfileInput = {
      name: name.trim(),
      description: description || null,
      guaranteedAvailabilityBp: guaranteedBp,
      measurementPeriod: period,
      reactionTimeMinutes: reactionMinutes ? Number(reactionMinutes) : null,
      resolutionTimeMinutes: resolutionMinutes ? Number(resolutionMinutes) : null,
      creditTiers: tiers,
      active,
    }
    if (isEdit && profile) {
      update.mutate(payload, {
        onSuccess: () => {
          toast({ title: 'Profil aktualisiert', tone: 'success' })
          onOpenChange(false)
        },
        onError: (err) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
      })
    } else {
      onSubmit(payload)
    }
  }

  function updateTier(index: number, key: keyof CreditTier, value: number) {
    setTiers((prev) => prev.map((t, i) => (i === index ? { ...t, [key]: value } : t)))
  }

  function addTier() {
    setTiers((prev) => [...prev, { thresholdPercentBp: 9000, creditPercentBp: 5000 }])
  }

  function removeTier(index: number) {
    setTiers((prev) => prev.filter((_, i) => i !== index))
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={isEdit ? `SLA-Profil bearbeiten` : 'SLA-Profil anlegen'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button variant="primary" size="sm" onClick={handleSubmit} loading={saving || update.isPending}>
            {isEdit ? 'Speichern' : 'Anlegen'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} required />
        <Textarea
          label="Beschreibung"
          rows={2}
          value={description ?? ''}
          onChange={(e) => setDescription(e.currentTarget.value)}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Garantierte Verfügbarkeit (%)"
            value={availabilityPercent}
            onChange={(e) => setAvailabilityPercent(e.currentTarget.value)}
            hint="z. B. 99,00 oder 99,90"
            required
          />
          <Select<'monthly' | 'quarterly' | 'yearly'>
            label="Messperiode"
            value={period}
            onValueChange={(v) => setPeriod(v)}
            options={PERIOD_OPTIONS as { value: 'monthly' | 'quarterly' | 'yearly'; label: string }[]}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Reaktionszeit (Minuten)"
            type="number"
            value={reactionMinutes}
            onChange={(e) => setReactionMinutes(e.currentTarget.value)}
            hint="optional"
          />
          <Input
            label="Wiederherstellungszeit (Minuten)"
            type="number"
            value={resolutionMinutes}
            onChange={(e) => setResolutionMinutes(e.currentTarget.value)}
            hint="optional"
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-small font-medium text-gray-900">Credit-Stufen</label>
            <Button variant="ghost" size="xs" leadingIcon={<Plus className="w-3 h-3" />} onClick={addTier}>
              Stufe hinzufügen
            </Button>
          </div>
          <div className="space-y-2">
            {tiers.map((tier, i) => (
              <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <Input
                  label={i === 0 ? 'Verfügbarkeit unter (%)' : undefined}
                  type="number"
                  step="0.01"
                  value={(tier.thresholdPercentBp / 100).toFixed(2)}
                  onChange={(e) => updateTier(i, 'thresholdPercentBp', Math.round(Number(e.currentTarget.value) * 100))}
                />
                <Input
                  label={i === 0 ? 'Gutschrift (%)' : undefined}
                  type="number"
                  step="0.01"
                  value={(tier.creditPercentBp / 100).toFixed(2)}
                  onChange={(e) => updateTier(i, 'creditPercentBp', Math.round(Number(e.currentTarget.value) * 100))}
                />
                <IconButton
                  icon={<Trash2 className="w-4 h-4" />}
                  aria-label="Stufe entfernen"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeTier(i)}
                />
              </div>
            ))}
            {tiers.length === 0 && (
              <p className="text-caption text-gray-500">
                Keine Stufen — Profil kann zugeordnet werden, aber es entstehen keine Gutschriften.
              </p>
            )}
          </div>
        </div>

        <label className="flex items-center gap-2 text-small text-gray-700">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.currentTarget.checked)} />
          Profil aktiv
        </label>
      </div>
    </Modal>
  )
}

// ─── Gutschriften-Tab ───────────────────────────────────────────────────────

function SlaCreditsTab() {
  const { toast } = useToast()
  const { data = [], isLoading, error, refetch } = useSlaCredits()
  const approve = useApproveSlaCredit()
  const reject = useRejectSlaCredit()
  const markInvoiced = useMarkSlaCreditInvoiced()
  const del = useDeleteSlaCredit()
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<SlaCreditRow | null>(null)

  const columns: ColumnDef<SlaCreditRow, unknown>[] = useMemo(
    () => [
      {
        id: 'contract',
        header: 'Vertrag',
        size: 220,
        cell: ({ row }) => (
          <div>
            <p className="text-small font-medium text-gray-900">
              {row.original.contract?.contractNumber ?? '—'}
            </p>
            <p className="text-caption text-gray-500">{row.original.contract?.name ?? ''}</p>
          </div>
        ),
      },
      {
        id: 'period',
        header: 'Messperiode',
        size: 180,
        cell: ({ row }) => {
          const start = new Date(row.original.credit.periodStart).toLocaleDateString('de-DE')
          const end = new Date(row.original.credit.periodEnd).toLocaleDateString('de-DE')
          return `${start} – ${end}`
        },
      },
      {
        id: 'availability',
        header: 'Verfügbarkeit',
        size: 130,
        cell: ({ row }) => {
          const bp = row.original.credit.measuredAvailabilityBp
          const variant = bp < 9500 ? 'danger' : bp < 9900 ? 'warning' : 'success'
          return (
            <div>
              <Badge variant={variant} size="sm">
                {fmtPercentBp(bp)}
              </Badge>
              <p className="text-caption text-gray-500 mt-1">
                Downtime: {row.original.credit.downtimeMinutes} Min.
              </p>
            </div>
          )
        },
      },
      {
        id: 'credit',
        header: 'Gutschrift',
        size: 160,
        cell: ({ row }) => (
          <div>
            <p className="text-small font-medium text-gray-900">
              {fmtCents(row.original.credit.creditAmountCents, row.original.credit.currency)}
            </p>
            <p className="text-caption text-gray-500">
              {fmtPercentBp(row.original.credit.creditPercentBp)} vom Vertragswert
            </p>
          </div>
        ),
      },
      {
        accessorKey: 'credit.status',
        header: 'Status',
        size: 130,
        cell: ({ row }) => <StatusBadge status={`sla_${row.original.credit.status}`} />,
      },
      {
        id: 'profile',
        header: 'Profil',
        size: 140,
        cell: ({ row }) => row.original.profile?.name ?? '—',
      },
      {
        id: 'actions',
        header: '',
        size: 220,
        cell: ({ row }) => {
          const status = row.original.credit.status
          const id = row.original.credit.id
          return (
            <div className="flex justify-end gap-1">
              {status === 'draft' && (
                <>
                  <Button
                    variant="primary"
                    size="xs"
                    leadingIcon={<CheckCircle2 className="w-3 h-3" />}
                    onClick={() =>
                      approve.mutate(id, {
                        onSuccess: () => toast({ title: 'Gutschrift freigegeben', tone: 'success' }),
                        onError: (err) =>
                          toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
                      })
                    }
                    loading={approve.isPending}
                  >
                    Freigeben
                  </Button>
                  <IconButton
                    icon={<XCircle className="w-4 h-4" />}
                    aria-label="Ablehnen"
                    variant="ghost"
                    size="xs"
                    onClick={() => reject.mutate(id)}
                  />
                </>
              )}
              {status === 'approved' && (
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() =>
                    markInvoiced.mutate(
                      { id, sapDocEntry: null },
                      {
                        onSuccess: () => toast({ title: 'Als verrechnet markiert', tone: 'success' }),
                        onError: (err) =>
                          toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
                      },
                    )
                  }
                >
                  Als verrechnet
                </Button>
              )}
              {status !== 'invoiced' && (
                <IconButton
                  icon={<Trash2 className="w-4 h-4" />}
                  aria-label="Löschen"
                  variant="ghost"
                  size="xs"
                  onClick={() => setConfirmDelete(row.original)}
                />
              )}
            </div>
          )
        },
      },
    ],
    [approve, reject, markInvoiced, toast],
  )

  if (isLoading) return <LoadingState label="Lade SLA-Gutschriften …" />
  if (error) {
    return (
      <ErrorState
        description="SLA-Gutschriften konnten nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
        onRetry={() => void refetch()}
      />
    )
  }

  return (
    <Card>
      <Card.Header>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-h4 font-semibold text-gray-900">SLA-Gutschriften</h3>
            <p className="text-small text-gray-500">
              Vertragsgutschriften pro Messperiode anhand gemessener Verfügbarkeit / Downtime.
            </p>
          </div>
          <Button variant="primary" size="sm" leadingIcon={<Plus className="w-4 h-4" />} onClick={() => setCreateOpen(true)}>
            Gutschrift erfassen
          </Button>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {data.length === 0 ? (
          <EmptyState
            icon={<ShieldAlert className="w-12 h-12 text-gray-400" />}
            title="Noch keine Gutschriften"
            description="Erfasse Downtime einer Messperiode, um eine SLA-Gutschrift zu berechnen und freizugeben."
          />
        ) : (
          <DataTable columns={columns} data={data} />
        )}
      </Card.Body>

      <CreateSlaCreditModal open={createOpen} onOpenChange={setCreateOpen} />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        title="Gutschrift löschen?"
        message={confirmDelete ? 'Diese Gutschrift wird unwiderruflich gelöscht.' : ''}
        variant="danger"
        confirmLabel="Löschen"
        loading={del.isPending}
        onConfirm={() => {
          if (!confirmDelete) return
          del.mutate(confirmDelete.credit.id, {
            onSuccess: () => {
              toast({ title: 'Gutschrift gelöscht', tone: 'success' })
              setConfirmDelete(null)
            },
            onError: (err) => {
              toast({ title: 'Fehler', description: err.message, tone: 'danger' })
              setConfirmDelete(null)
            },
          })
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </Card>
  )
}

// ─── Gutschrift-Anlegen Modal ───────────────────────────────────────────────

function CreateSlaCreditModal({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { toast } = useToast()
  const { data: profiles = [] } = useSlaProfiles()
  const { data: contracts = [] } = useContracts({ direction: 'outbound', status: 'active' })
  const preview = usePreviewSlaCredit()
  const create = useCreateSlaCredit()

  const [contractId, setContractId] = useState('')
  const [profileId, setProfileId] = useState('')
  const [periodStart, setPeriodStart] = useState(firstOfMonthIso(1))
  const [periodEnd, setPeriodEnd] = useState(endOfMonthIso(1))
  const [downtimeMinutes, setDowntimeMinutes] = useState('0')
  const [notes, setNotes] = useState('')
  const [tanssTicketIds, setTanssTicketIds] = useState('')
  const [previewResult, setPreviewResult] = useState<SlaCreditPreview | null>(null)

  const contractOptions = useMemo(
    () => [
      { value: '', label: '— Vertrag wählen —' },
      ...contracts.map((c) => ({ value: c.id, label: `${c.contractNumber} · ${c.customerName ?? c.partyName ?? ''}` })),
    ],
    [contracts],
  )
  const profileOptions = useMemo(
    () => [
      { value: '', label: '— Profil aus Vertrag verwenden —' },
      ...profiles.map((p) => ({ value: p.id, label: p.name })),
    ],
    [profiles],
  )

  function buildInput(): CreateSlaCreditInput | null {
    if (!contractId) {
      toast({ title: 'Vertrag wählen', tone: 'danger' })
      return null
    }
    if (!periodStart || !periodEnd) {
      toast({ title: 'Messperiode unvollständig', tone: 'danger' })
      return null
    }
    return {
      contractId,
      profileId: profileId || null,
      periodStart,
      periodEnd,
      downtimeMinutes: Number(downtimeMinutes) || 0,
      notes: notes || null,
      tanssTicketIds: tanssTicketIds || null,
    }
  }

  function handlePreview() {
    const input = buildInput()
    if (!input) return
    preview.mutate(input, {
      onSuccess: (data) => setPreviewResult(data),
      onError: (err) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
    })
  }

  function handleCreate() {
    const input = buildInput()
    if (!input) return
    create.mutate(input, {
      onSuccess: () => {
        toast({ title: 'Gutschrift angelegt', tone: 'success' })
        onOpenChange(false)
        setPreviewResult(null)
      },
      onError: (err) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
    })
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="SLA-Gutschrift erfassen"
      size="lg"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button variant="secondary" size="sm" onClick={handlePreview} loading={preview.isPending}>
            Vorschau berechnen
          </Button>
          <Button variant="primary" size="sm" onClick={handleCreate} loading={create.isPending}>
            Gutschrift anlegen
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Select label="Vertrag" value={contractId} onValueChange={setContractId} options={contractOptions} />
        <Select label="SLA-Profil (optional)" value={profileId} onValueChange={setProfileId} options={profileOptions} />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Messperiode Beginn"
            type="date"
            value={periodStart}
            onChange={(e) => setPeriodStart(e.currentTarget.value)}
            required
          />
          <Input
            label="Messperiode Ende"
            type="date"
            value={periodEnd}
            onChange={(e) => setPeriodEnd(e.currentTarget.value)}
            required
          />
        </div>
        <Input
          label="Downtime in Minuten"
          type="number"
          value={downtimeMinutes}
          onChange={(e) => setDowntimeMinutes(e.currentTarget.value)}
          hint="Aus dieser Dauer wird die gemessene Verfügbarkeit berechnet."
        />
        <Input
          label="Verknüpfte TANSS-Tickets"
          value={tanssTicketIds}
          onChange={(e) => setTanssTicketIds(e.currentTarget.value)}
          hint="Komma-getrennte Ticket-IDs (optional)."
        />
        <Textarea
          label="Notizen"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />

        {previewResult && (
          <Card className="border-blue-200 bg-blue-50">
            <Card.Body className="space-y-1 text-small text-blue-900">
              <div>
                Gemessene Verfügbarkeit:{' '}
                <strong>{fmtPercentBp(previewResult.measuredAvailabilityBp)}</strong>
                {previewResult.meetsSla
                  ? ' — SLA eingehalten, keine Gutschrift'
                  : ' — SLA verletzt'}
              </div>
              <div>
                Gutschrift-Anteil: <strong>{fmtPercentBp(previewResult.creditPercentBp)}</strong>
              </div>
              <div>
                Gutschriftbetrag: <strong>{fmtCents(previewResult.creditAmountCents)}</strong>
                {previewResult.monthlyContractValueCents > 0 && (
                  <> (auf Basis Vertragswert {fmtCents(previewResult.monthlyContractValueCents)})</>
                )}
              </div>
            </Card.Body>
          </Card>
        )}
      </div>

    </Modal>
  )
}
